/** @format */
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { Collapse } from "antd";
import CountryDropdown from "country-dropdown-with-flags-for-react";
import { React, useEffect, useState } from "react";
import { usePaymentInputs } from "react-payment-inputs";
import images from "react-payment-inputs/images";
import { toast } from "react-toastify";
import { Accordion } from "rsuite";
import { Spin } from "antd";
import { isEmpty } from "lodash";
import { BsCardChecklist } from "react-icons/bs";
import {
    useCreateSaveDetails,
    useSavedDetails,
} from "../hooks/useSavedDetails";
import axiosInstance from "../lib/axios";
import { useAuthState } from "../state/useAuthState";
import { useGeneralState } from "../state/useGeneralState";
import Header from "./Header";
import { PaymentOnlyMethod } from "./PaymentMethods";

function Card({
    onPaymentSuccess,
    onlyAllowedPayment,
    country,
    stripeId,
    setNameGlobal,
    customerEmail,
    customerName,
    alpha2CountryCode,
    merchantDetails,
}) {
    const { transactionBaseUrl } = useGeneralState();
    const [consentResponse, setConsentResponse] = useState(false);
    const [pageStep] = useState(1);
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState();
    const { clientSecret } = useAuthState();
    const [cardNumber, setCardNumber] = useState("");
    const [name, setName] = useState(customerName | ""); // for non NGN countries [name, setName] = useState("");
    const [expiry, setExpiry] = useState("");
    const [cardHolderAddress, setCardHolderAddress] = useState("");
    const [townOrCity, setTownOrCity] = useState("");
    const [stateAddress, setStateAddress] = useState("");
    const [zipCode, setZipCode] = useState("");
    // const [localCustomerName, setLocalCustomerName] = useState(customerName || "");
    const saveDetails = useCreateSaveDetails({
        email: customerEmail,
        firstName: String(name).split(" ")[0] ?? "",
        lastName:
            String(name)
                .split(" ")
                .filter((_, i) => i > 0)
                .join("") ?? "",
        address: cardHolderAddress,
        town: townOrCity,
        state: stateAddress,
        zipCode: zipCode,
        paymentMethods: [PaymentOnlyMethod.xcel, PaymentOnlyMethod.card],
    });

    const handleConsentResponse = (event) => {
        setConsentResponse(event.target.checked);
    };

    let savedDetails = useSavedDetails({
        userProfile: customerEmail,
        paymentType: PaymentOnlyMethod.card,
    });

    const [countryVal, setCountry] = useState(alpha2CountryCode); // for non NGN countries [country, setCountry] = useState("");
    const {
        meta,
        getCardNumberProps,
        getExpiryDateProps,
        getCVCProps,
        getCardImageProps,
    } = usePaymentInputs();
    const [cvc, setCvc] = useState("");
    const [savedCvv, setSavedCvv] = useState(false);
    const [activeSavedCard, setActiveSavedCard] = useState("");
    // const [decryptedData, setDecryptedData] = useState();

    useEffect(() => {
        if (customerName) {
            setName(customerName); // Sync prop changes to state
        }
    }, [customerName]);

    useEffect(() => {
        if (savedDetails?.length > 0) {
            savedCardSelected(0);
        }
    }, [savedDetails])

    function closeCardInputCollapse() {
        const collapseItem = document.querySelector('.ant-collapse-item');
        if (collapseItem) {
            const isActive = collapseItem.classList.contains('ant-collapse-item-active');
            if (isActive) {
                collapseItem.querySelector('.ant-collapse-header').click();
            }
        }
    }

    const fetchDecryptedCardDetails = async () => {
        const result = await axiosInstance.get(
            `https://api.xcelapp.com/transactions-service/paygate/user/${savedDetails[activeSavedCard]?.email}/cards/${savedDetails[activeSavedCard]?._id}`
        );

        return result?.data?.data;
    };

    const savedCardSelected = (index) => {
        setActiveSavedCard(index);
        setSavedCvv(true);
    }

    const handleSubmit = async (event) => {
        try {
            // We don't want to let default form submission happen here,
            // which would refresh the page.
            setProcessing(true);

            if (!stripe || !elements) {
                // Stripe.js has not yet loaded.
                // Make sure to disable form submission until Stripe.js has loaded.
                return;
            }
            let decryptedData = null;
            if (savedCvv) {
                decryptedData = await fetchDecryptedCardDetails();
                // setDecryptedData(result);
            }

            //TODO: use tokenized card instead

            if (!savedCvv) {
                if (isEmpty(name)) {
                    toast.warning("Please enter your card name!");
                    setProcessing(false);
                    return;
                } else if (isEmpty(cardNumber)) {
                    toast.warning("Please enter your card number!");
                    setProcessing(false);
                    return;
                } else if (isEmpty(expiry)) {
                    toast.warning("Please enter your card expiry date!");
                    setProcessing(false);
                    return;
                } else if (isEmpty(cvc)) {
                    toast.warning("Please enter your card cvc!");
                    setProcessing(false);
                    return;
                }

                if (country !== "NGN") {
                    if (isEmpty(cardHolderAddress)) {
                        toast.warning("Please enter your card holder address!");
                        setProcessing(false);
                        return;
                    } else if (isEmpty(townOrCity)) {
                        toast.warning("Please enter your town or city!");
                        setProcessing(false);
                        return;
                    } else if (isEmpty(stateAddress)) {
                        toast.warning(
                            "Please enter your state or region or county!"
                        );
                        setProcessing(false);
                        return;
                    } else if (isEmpty(zipCode)) {
                        toast.warning("Please enter your postal or zip code!");
                        setProcessing(false);
                        return;
                    }
                }
            }

            const exp_month = savedCvv
                ? Number(decryptedData?.expirationDate.split("/")[0].trim())
                : Number(expiry.split("/")[0].trim());
            const exp_year = savedCvv
                ? Number(
                      decryptedData?.expirationDate
                          .split("/")[1]
                          .trim()
                          .replace(/\s+/g, "")
                  )
                : Number(expiry.split("/")[1].trim().replace(/\s+/g, ""));


            const result = await axiosInstance.post(
                `${transactionBaseUrl}stripe/card-token`,
                {
                    number: savedCvv ? decryptedData?.metadata.cardNumber : cardNumber,
                    cvc: savedCvv ? decryptedData?.metadata.cvv : cvc,
                    exp_month,
                    exp_year,
                    stripeId,
                }
            );

            if (result?.error?.message) {
                toast.warning(
                    result?.error?.message?.raw?.message ??
                        result?.response?.data?.message
                );
                setProcessing(false);
                return;
            }

            const { error, paymentIntent } = await stripe.confirmCardPayment(
                clientSecret,
                {
                    payment_method: {
                        card: { 
                            token: result?.data?.cardTokenized?.id 
                        },
                        billing_details: {
                            name: name,
                            email: customerEmail,
                            address: {
                                line1: cardHolderAddress,
                                line2: '',
                                city: townOrCity,
                                state: stateAddress,
                                postal_code: zipCode,
                                country: alpha2CountryCode,
                            },
                            phone: ''
                        }
                    },
                }
            );

            if (error) {
                toast.error(error.message);
                setProcessing(false);

                return;
            }
            setProcessing(false);
            onPaymentSuccess();
            if (consentResponse) {
                await saveDetails.mutateAsync({
                    currentPaymentMethod: PaymentOnlyMethod.card,
                    cvv: cvc,
                    expirationDate: expiry,
                    cardNumber,
                    address: cardHolderAddress,
                    town: townOrCity,
                    state: stateAddress,
                    zipCode,
                });
            }
        } catch (error) {
            toast.warning(
                error?.response?.data?.message ??
                    error?.message?.raw?.message ??
                    error?.message
            );
            setProcessing(false);
        }
    };

    const cardComponent = () => {
        return (
            <>
                <div className="my-4">
                    <label
                        className="block text-gray-700 text-sm font-bold mb-2"
                        for="username">
                        Card Number
                    </label>
                    <div className="">
                        <svg
                            {...getCardImageProps({ images })}
                            className="absolute mt-4 ml-1"
                        />
                        <input
                            className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline pl-8"
                            id="username"
                            type="text"
                            placeholder="Card Number"
                            {...getCardNumberProps({
                                onChange: (e) => setCardNumber(e.target.value),
                            })}
                        />
                    </div>
                </div>
                <div className="my-4">
                    <div className="flex flex-wrap -mx-3">
                        <div className="w-full md:w-1/2 px-3 mb-4 md:mb-0">
                            <label
                                className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                                for="expiry-date">
                                Expiry Date
                            </label>
                            <input
                                className="appearance-none block w-full shadow text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                                id="expiry-date"
                                type="text"
                                placeholder="MM/YY"
                                {...getExpiryDateProps({
                                    onChange: (e) => setExpiry(e.target.value),
                                })}
                            />
                        </div>
                        <div className="w-full md:w-1/2 px-3">
                            <label
                                className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                                for="cvc">
                                CVV
                            </label>
                            <input
                                className="appearance-none block w-full shadow text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                                id="cvc"
                                type="text"
                                {...getCVCProps({
                                    onChange: (e) => {
                                        setCvc(e.target.value);
                                        setSavedCvv(false)
                                    },
                                })}
                                placeholder="CVV"
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const cardInputComponent = (
        <>
            <div className="my-4">
                <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    for="username">
                    Card Holder's Name
                </label>
                <input
                    className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="username"
                    type="text"
                    value={name}
                    placeholder="Card Holder's Name"
                    onChange={(e) => {
                        setName(e.target.value);
                        setNameGlobal(e.target.value);
                    }}
                />
            </div>
            {cardComponent()}
        </>
    );

    const cardInputComponentUk = (
        <>
            <div className="my-4">
                <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    for="username">
                    Card Holder's Name
                </label>
                <input
                    className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="username"
                    type="text"
                    placeholder="Card Holder's Name"
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        setNameGlobal(e.target.value);
                    }}
                />
            </div>

            {cardComponent()}

            <div className="my-2">
                <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    for="address">
                    Card Holder's Address
                </label>
                <input
                    className="shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="address"
                    type="text"
                    onChange={(e) => {
                        setCardHolderAddress(e.target.value);
                    }}
                    placeholder="Card Holder's Address"
                />
            </div>
            <div className="my-4  ">
                <div className="flex flex-wrap -mx-3 mb-2">
                    <div className="w-full md:w-1/2 px-3 mb-4 md:mb-0">
                        <label
                            className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                            for="town">
                            Town/City
                        </label>
                        <input
                            className="appearance-none block w-full shadow text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            id="town"
                            type="text"
                            onChange={(e) => {
                                setTownOrCity(e.target.value);
                            }}
                            placeholder="Town/City"
                        />
                    </div>
                    <div className="w-full md:w-1/2 px-3">
                        <label
                            className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                            for="state">
                            State/ Region/ County
                        </label>
                        <input
                            className="appearance-none block w-full shadow text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            id="state"
                            type="text"
                            onChange={(e) => {
                                setStateAddress(e.target.value);
                            }}
                            placeholder="State/ Region/ County"
                        />
                    </div>
                </div>
            </div>
            <div className="my-4  ">
                <div className="flex flex-wrap -mx-3 mb-2">
                    <div className="w-full md:w-1/2 px-3 mb-4 md:mb-0">
                        <label
                            className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                            for="zip-code">
                            Postal/ Zip Code
                        </label>
                        <input
                            className="appearance-none block w-full shadow text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                            id="zip-code"
                            type="text"
                            onChange={(e) => {
                                setZipCode(e.target.value);
                            }}
                            placeholder="Postal/ Zip Code"
                        />
                    </div>

                    <div className="w-full md:w-1/2 px-3 ">
                        <label
                            className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                            for="country">
                            Country
                        </label>
                        <div className="appearance-none block w-full shadow text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500">
                            <CountryDropdown
                                id="UNIQUE_ID"
                                className="w-full h-full outline-none border-none"
                                preferredCountries={["gb", "us"]}
                                value=""
                                handleChange={(e) => {
                                    setCountry(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <div className=" w-full px-3 py-2 flex items-center text-center font-medium gap-2">
                        <input
                            type="checkbox"
                            name="description"
                            value={"Yes"}
                            onChange={(e) => {
                                handleConsentResponse(e);
                            }}
                        />
                        Save card details for future payments
                    </div>
                </div>
            </div>
        </>
    );

    const creditCardsSaved = (
        <>
            <Accordion defaultActiveKey={1}>
                {(savedDetails ?? []).map((i, index) => {
                    return (
                        <Accordion.Panel
                            header={
                                <div onClick={() => 
                                    {
                                        savedCardSelected(index);
                                        const collapseButton = document.getElementById('hidden-collapse-toggle-btn');
                                        if (collapseButton) {
                                            collapseButton.click();
                                        }
                                    }
                                }
                                    className="flex flex-row items-center gap-2 cursor-pointer rounded-md  transition-all -py-20">
                                    <BsCardChecklist size={10} />
                                    <div>{String(i.cardNumber)}</div>
                                </div>
                            }
                            eventKey={index + 1}>
                        </Accordion.Panel>
                    );
                })}
            </Accordion>
        </>
    );

    if (country === "NGN" && pageStep == 1) {
        return (
            <div
                className={`payment-section m-auto ${
                    isEmpty(onlyAllowedPayment) ? "mt-12" : "mt-0"
                }`}>

                <div
                    className={`${onlyAllowedPayment ? "hidden md:flex" : ""}`}>
                    <Header
                        title={
                            merchantDetails?.ent_type === "RELIGIOUS"
                                ? "Give  with Debit/ Credit Card"
                                : "Pay with Debit/ Credit Card"
                        }
                    />
                </div>

                <div className="p-5 w-full">
                    <p className={`text-gray-400`}>
                        {!isEmpty(savedDetails)
                            ? "Select your saved card to proceed"
                            : "Please enter your card details to make payment"}
                    </p>

                    <div className="my-2">{creditCardsSaved}</div>

                    {!isEmpty(savedDetails) ? (
                        <div className="-ml-4">
                            <div className="mt-2" />
                            <Collapse
                                ghost
                                onChange={(key) => {
                                    if (key.length > 0) {
                                        setSavedCvv(false);
                                    }
                                }}
                                items={[
                                    {
                                        key: "1",
                                        label: "Pay with another card",
                                        children: <>{cardInputComponent}</>,
                                    },
                                ]}
                            />
                        </div>
                    ) : (
                        <>{cardInputComponent}</>
                    )}

                    <div className="my-8">
                        <button
                            className="bg-[#222121] hover:bg-[#323131] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
                            type="button"
                            onClick={handleSubmit}
                            loading={processing}
                            disabled={!stripe || processing || !cvc}>
                            {!processing && 'Proceed'} {processing && <Spin />}
                        </button>
                    </div>
                </div>
            </div>
        );
    } else if (country !== "NGN" && pageStep == 1) {
        return (
            <div
                className={`payment-section m-auto ${
                    isEmpty(onlyAllowedPayment) ? "mt-12" : "mt-0"
                }`}>

                <div
                    className={`${onlyAllowedPayment ? "hidden md:flex" : ""}`}>
                    <Header
                        title={
                            merchantDetails?.ent_type === "RELIGIOUS"
                                ? "Give  with Debit/ Credit Card"
                                : "Pay with Debit/ Credit Card"
                        }
                    />
                </div>

                <div className="p-5 w-full">
                    <p className={`text-gray-400`}>
                        {!isEmpty(savedDetails)
                            ? "Select your saved card to proceed"
                            : "Please enter your card details to make payment"}
                    </p>

                    <div className="my-2">{creditCardsSaved}</div>

                    {!isEmpty(savedDetails) ? (
                        <div className="-ml-4">
                            <button className="hidden" id={'hidden-collapse-toggle-btn'}
                                onClick={closeCardInputCollapse}>
                            </button>
                            <div className="mt-2" />
                            <Collapse
                                ghost
                                onChange={(key) => {
                                    if (key.length > 0) {
                                        setSavedCvv(false);
                                    }
                                }}
                                items={[
                                    {
                                        key: "1",
                                        label: "Pay with another card",
                                        children: <>{cardInputComponentUk}</>,
                                    },
                                ]}
                            />
                        </div>
                    ) : (
                        <>{cardInputComponentUk}</>
                    )}

                    <div className="my-8">
                        <button
                            className="bg-[#0095FF] hover:bg-[#0095FFb6] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
                            type="button"
                            onClick={handleSubmit}
                            loading={processing}
                            disabled={!stripe || processing}>
                            {!processing && 'Proceed'} {processing && <Spin />}
                        </button>
                    </div>
                </div>
            </div>
        );
    } else if ((pageStep = 2)) {
        return (
            <div className="payment-section m-auto mt-12">
                <Header
                    title={
                        merchantDetails?.ent_type === "RELIGIOUS"
                            ? "Give  with Debit/ Credit Card"
                            : "Pay with Debit/ Credit Card"
                    }
                />

                <div className="p-5 w-full">
                    <p className="text-gray-400">
                        Please enter the OTP sent to your XCel app
                    </p>
                    <div className="my-7">
                        <label
                            className="block text-gray-700 text-sm font-bold mb-2"
                            for="username">
                            Enter OTP
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            id="username"
                            type="password"
                            placeholder="******"
                        />
                    </div>
                    <div className="my-8">
                        <button
                            className="bg-[#0095FF] hover:bg-[#0095FFb6] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
                            type="button"
                            onClick={handleSubmit}
                            loading={processing}
                            disabled={!stripe || processing}>
                            Make Payment
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default Card;
